import type { FC, ReactNode } from 'react';

import { Typography } from '@mui/material';
import { ConfirmationDialog } from '@truvhq/ui';
import { clsx } from 'clsx';

import { useScreenSize } from 'shared/lib/screen/useScreenSize';
import { SwipeSheet } from 'shared/ui/SwipeSheet';

type Props = {
  open: boolean;
  title: string;
  /** A caption for the body below it, or the whole message where there is no body */
  subtitle?: ReactNode;
  /** Buttons, bare: each shell lays them out, so they need no wrapper and no `fullWidth` */
  actions?: ReactNode;
  /** Drop the close control where dismissing should go through an action instead */
  withCloseIcon?: boolean;
  onClose: VoidFunction;
  children?: ReactNode;
};

/**
 * One dialog in two frames: centered on a desktop, risen from the bottom edge on a phone. The title,
 * body and actions are the same either way — only the frame around them changes.
 *
 * A no-op `onClose` locks both frames alike, since MUI routes backdrop clicks and Escape through
 * `onClose` on the `Modal` that `Dialog` and `Drawer` both wrap.
 */
export const ResponsiveDialog: FC<Props> = ({
  open,
  title,
  subtitle,
  actions,
  withCloseIcon = true,
  onClose,
  children,
}) => {
  const { isMobile } = useScreenSize();

  if (isMobile) {
    return (
      <SwipeSheet footer={actions} open={open} title={title} withCloseIcon={withCloseIcon} onClose={onClose}>
        {/* The dialog hangs its subtitle under a left-aligned title; the sheet centers its title, so
            the subtitle is centered to match, and only has to clear a body that follows it */}
        {!!subtitle && (
          <Typography className={clsx('text-center text-grey60', children && 'mb-4')}>{subtitle}</Typography>
        )}
        {children}
      </SwipeSheet>
    );
  }

  return (
    <ConfirmationDialog
      actions={actions}
      // ConfirmationDialog always renders its content block, which would leave a dialog carrying
      // only a subtitle with an empty 24px band between the title and the actions
      className={clsx(!children && '[&_.MuiDialogContent-root]:hidden')}
      open={open}
      subtitle={subtitle}
      title={title}
      withCloseIcon={withCloseIcon}
      onClose={onClose}
    >
      {/* The theme leaves the content's bottom padding to the actions bar below it, so a dialog
          without actions has to supply its own */}
      {children && !actions ? <div className="pb-6">{children}</div> : children}
    </ConfirmationDialog>
  );
};
